'use client';

import { createContext, FC, ReactNode, useContext, useRef } from 'react';

import { CaseStudyDto } from '@/types/case-studies';

type TakeCardPreview = (caseStudy: CaseStudyDto, claimKey: string) => CaseStudyDto | undefined;

const CardPreviewUsageContext = createContext<TakeCardPreview | undefined>(undefined);

export const CardPreviewUsageProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const usageCountByCaseIdRef = useRef(new Map<number, number>());
	const claimsRef = useRef(new Map<string, CaseStudyDto | undefined>());

	const takeCardPreview: TakeCardPreview = (caseStudy, claimKey) => {
		if (claimsRef.current.has(claimKey)) return claimsRef.current.get(claimKey);

		const previews = caseStudy.cardPreview;
		if (!previews?.length) {
			claimsRef.current.set(claimKey, undefined);
			return undefined;
		}

		const nextIndex = usageCountByCaseIdRef.current.get(caseStudy.id) ?? 0;
		usageCountByCaseIdRef.current.set(caseStudy.id, nextIndex + 1);

		const preview = previews[nextIndex] ?? previews[0];
		const claimed = { ...caseStudy, cardPreview: [preview] };
		claimsRef.current.set(claimKey, claimed);

		return claimed;
	};

	return (
		<CardPreviewUsageContext.Provider value={takeCardPreview}>
			{children}
		</CardPreviewUsageContext.Provider>
	);
};

export const useCardPreviewUsage = (): TakeCardPreview => {
	const takeCardPreview = useContext(CardPreviewUsageContext);

	if (!takeCardPreview)
		throw new Error('useCardPreviewUsage must be used within CardPreviewUsageProvider');

	return takeCardPreview;
};
